/**
 * Responsibility: show what the scanner thinks the card is, and let you say.
 *
 * **A shortlist, and a person who confirms it.** Measured on 406 real
 * seller photographs against the whole four-million-card index, the
 * matcher's first guess is right 42% of the time and the right card is
 * in the first five 56% of the time. An interface that filed the top
 * answer silently would therefore be wrong on more than half of them,
 * and wrong in the worst possible way: a four pound card recorded as
 * the four hundred pound printing of itself, by somebody who never saw
 * the choice.
 *
 * Those numbers are held down by coverage rather than by ranking - only
 * 219,000 of the four million cards have had their picture hashed yet,
 * and against a pool where the right card's picture is definitely held
 * the same matcher gets 59% first and 65% within five. The shortlist is
 * the right shape either way; the percentages should improve as the
 * harvest runs, and this comment should be corrected when they do.
 *
 * So every candidate is shown with its picture, because the picture is what
 * a person can check in half a second and no amount of confidence scoring
 * can replace.
 *
 * **What it read is shown too.** "I read DIsSOLVEROCK and 021/162" explains
 * a wrong answer and lets somebody type the right thing; "no match" explains
 * nothing and leaves them stuck.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

import type { IdentifiedCard, IdentifyResult } from '../../api/client';

type IdentifyPanelProps = {
  result: IdentifyResult | null;
  busy: boolean;
  /** Add this card to the collection, after the grade has been asked for. */
  onAdd: (card: IdentifiedCard) => void;
  /** Open the full card - prices, population, history - as search does. */
  onOpen: (card: IdentifiedCard) => void;
};

/**
 * How sure, in words. Never a percentage: nothing here is calibrated.
 *
 * **Certainty is the hub's word, not this component's.** This used to
 * hold its own bit threshold and call a card at 14 bits "looks right"
 * while the matcher reserved that for 8 with a clear margin - two
 * answers to one question, in two languages, drifting apart on their
 * own. `decided` is the matcher's own judgement and is swept against
 * precision; this says it in English.
 */
function confidence(card: IdentifiedCard, first: boolean, decided: boolean): string {
  if (card.number_agrees) return 'number matches';
  if (card.distance === null) return 'from the name';
  if (first) return decided ? 'looks right' : 'closest picture';
  return 'also similar';
}

function Candidate({
  card,
  first,
  decided,
  onAdd,
  onOpen,
}: {
  card: IdentifiedCard;
  first: boolean;
  /** The matcher's own verdict on the top answer. */
  decided: boolean;
  onAdd: () => void;
  onOpen: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: first ? 'primary.main' : 'divider',
        borderWidth: first ? 2 : 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* The picture first and largest. It is what a person actually
            checks, and everything else on this row is a footnote to it. */}
        <Box
          component="img"
          src={card.image_url ?? ''}
          alt=""
          sx={{
            width: 64,
            height: 89,
            objectFit: 'cover',
            borderRadius: 1,
            bgcolor: 'action.hover',
            flexShrink: 0,
          }}
        />
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {card.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {card.set_name}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant={first ? 'filled' : 'outlined'}
              color={card.number_agrees ? 'success' : 'default'}
              label={confidence(card, first, decided)}
            />
          </Stack>
        </Stack>
        <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Prices, population and history">
            <Button size="small" variant="text" onClick={onOpen} startIcon={<SearchIcon />}>
              Open
            </Button>
          </Tooltip>
          <Button size="small" variant="contained" onClick={onAdd} startIcon={<AddIcon />}>
            This one
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function IdentifyPanel({ result, busy, onAdd, onOpen }: IdentifyPanelProps) {
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  // **The bar sits above the answer rather than replacing it.** With the
  // camera firing by itself a scan starts every few seconds, and blanking
  // the shortlist each time made the panel flicker between a result and
  // an empty box while somebody was still reading it.
  const working = busy ? <LinearProgress /> : null;
  if (!result) return working;

  if (result.candidates.length === 0) {
    return (
      <Alert
        severity="warning"
        action={
          <Button size="small" onClick={() => void navigate('/search')}>
            Search instead
          </Button>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {result.card_found ? 'No card matched that photograph.' : 'No card found in the frame.'}
        </Typography>
        <Typography variant="body2">
          {result.read_name || result.read_numbers.length > 0
            ? `It read "${result.read_name}"${
                result.read_numbers.length > 0 ? ` and ${result.read_numbers[0]}` : ''
              }, which matched nothing in the catalogue.`
            : 'Nothing legible was read off it. Fill the frame with the card, and sharp matters more than bright.'}
        </Typography>
      </Alert>
    );
  }

  // Shown expanded when the top answer is not clearly ahead, because that
  // is exactly when the choice is the person's to make rather than a
  // confirmation of one already made.
  const open = showAll || !result.decided;
  const shown = open ? result.candidates : result.candidates.slice(0, 1);

  return (
    <Stack spacing={1.5}>
      {working}
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">
          {result.decided ? 'This looks like' : 'One of these'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          read &ldquo;{result.read_name || 'nothing'}&rdquo;
          {result.read_numbers.length > 0 ? ` · ${result.read_numbers[0]}` : ''}
        </Typography>
      </Stack>

      {shown.map((card, index) => (
        <Candidate
          key={card.product_key}
          card={card}
          first={index === 0}
          decided={result.decided}
          onAdd={() => onAdd(card)}
          onOpen={() => onOpen(card)}
        />
      ))}

      {!open && result.candidates.length > 1 ? (
        <Button size="small" variant="text" onClick={() => setShowAll(true)}>
          {`Not it? Show ${result.candidates.length - 1} more`}
        </Button>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        Check the picture before adding. The scanner reads the name and the collector
        number and then compares the artwork, which tells most printings apart and
        not all of them.
      </Typography>
    </Stack>
  );
}
